import { saberChance } from './saberes.ts';
import {
  ERA_DELVES,
  LOOT_POWER,
  RELIC_DROPS,
  SIMA,
  SIMA_CHEST,
  SIMA_LOOT,
  SIMA_POWER,
  SIMA_WEEK_CHEST,
  STRATUM,
  upToEra,
  type NightmareType,
  type Rarity,
  type SimaCreature,
  type GearSlot,
} from '@somnhold/content';
import { PANOPLY } from '@somnhold/content';
import { addScraps } from './scraps.ts';
import type { BattleParams } from '../battle/create.ts';
import { SIMA_RADIUS, simaFloor, simaMap, simaOrder } from '../battle/sima.ts';
import { idiv, imax, imin, mulBp } from '../math/fixed.ts';
import { snapshot } from '../math/hash.ts';
import { deriveSeed, rngChance } from '../math/rng.ts';
import type { BattleState } from '../types.ts';
import { addRes, event, findPlayer, heroName } from './core.ts';
import { eraOf, groupReached } from './eras.ts';
import { rollRelic } from './incursion.ts';
import { giveItem, lootCtx, rollItem } from './items.ts';
import { bigParty, crowd } from './party.ts';
import { afflictAfter, fightKit, grantXp } from './progress.ts';
import type { CampaignState, Ms, SimaRecord } from './types.ts';
import { tallyDeeds } from './deeds.ts';
import { tallyFeats } from './feats.ts';
import { logOuting } from './outings.ts';

/*
 * The Chasm (la Sima, docs/SIMA.md), on the campaign's side: when it opens, the group's mark (ever and this week),
 * the thresholds a run can start from, the week's chest for whoever went down, and what a run brings back.
 */

/** The campaign week a day falls in (as the Furrows). */
export const simaWeek = (day: number): number => idiv(day, SIMA.weekDays);
/** The week's seed: the order of the strata and every floor's signs. */
export const simaWeekSeed = (s: CampaignState, week: number): number => deriveSeed(s.seed, 17000 + week);

/** Nightmares every era has (era I's own and the first ones). */
const ERA_ONE: NightmareType[] = ['goblin', 'wolf', 'skeleton', 'wraith', 'gargoyle', 'slime', 'stalker', 'slinger', 'crow', 'mourner', 'ogre'];

/** What the group's era lets come down there (the Chasm's own creatures always come). */
export const simaAllowed = (s: CampaignState): (NightmareType | SimaCreature)[] => {
  const more = upToEra(ERA_DELVES, eraOf(s)) ?? {};
  return [...new Set([...ERA_ONE, ...(Object.keys(more) as NightmareType[])])].sort();
};

/**
 * Open once the group has won the deep crypt to its bottom: noted when it happens; before this feature, any hero who
 * still has a seal of the deep crypt, or a full descent among the group's recent outings, says it did.
 */
export const simaOpen = (s: CampaignState): boolean =>
  groupReached(s, SIMA.minGroup) && (!!s.sima?.open || s.players.some((p) => (p.hero.seals?.deep ?? 0) > 0) || !!s.outings?.recent.some((r) => r.key === 'crypt3' && r.won && (r.note ?? 0) >= 3));

const fresh = (week: number, old?: SimaRecord): SimaRecord => ({
  open: old?.open ?? false,
  best: old?.best ?? 0,
  marks: old?.marks ?? [],
  stones: old?.stones ?? [],
  runs: old?.runs ?? 0,
  week,
  weekBest: 0,
  went: [],
});

/** The group's Chasm for the week of `day` (a new week starts its mark at zero; the rest carries over). */
export const simaRecord = (s: CampaignState, day: number): SimaRecord => {
  const week = simaWeek(day);
  return s.sima && s.sima.week === week ? s.sima : fresh(week, s.sima);
};

/** Where a run can start: the first floor, or just past any guardian the group ever felled. */
export const simaStarts = (s: CampaignState): number[] => {
  const best = s.sima?.best ?? 0;
  const out = [1];
  for (let t = SIMA.threshold; t <= best; t += SIMA.threshold) out.push(t + 1);
  return out;
};

/**
 * A new week: whoever went down last week gets the week's chest by the group's mark of it (once, even if they never
 * come back); the new week starts at zero. True when anything changed.
 */
export const settleSimaWeek = (s: CampaignState, day: number, now: Ms): boolean => {
  const rec = s.sima;
  if (!rec || rec.week === simaWeek(day)) return false;
  if (rec.weekBest > 0 && rec.went.length) {
    const row = [...SIMA_WEEK_CHEST].reverse().find((r) => rec.weekBest >= r.from);
    if (row)
      for (const pid of rec.went) {
        const p = findPlayer(s, pid);
        if (!p || p.left) continue;
        grantXp(s, pid, row.xp, now);
        if (row.embers) p.hero.embers = (p.hero.embers ?? 0) + row.embers;
        if (row.shards) p.hero.shards = (p.hero.shards ?? 0) + row.shards;
        if (row.piece) giveItem(s, p, rollItem(s, s.rng, row.piece as Rarity, simaCtx(s, p, simaPower(rec.weekBest), day)), now);
        if (row.relicBp) rollRelic(s, pid, row.relicBp, now);
        event(s, now, 'simaWeek', { pid, data: { mark: rec.weekBest } });
      }
    if (row?.dust) addRes(s.storage, { wood: 0, stone: 0, crystal: 0, dust: row.dust });
    // The week's chest, from mark 10: a saber now and then, for those who went down (docs/SABERES.md §5).
    saberChance(s, 'sima', rec.went, day, now, rec.weekBest);
  }
  s.sima = fresh(simaWeek(day), rec);
  s.version++;
  return true;
};

/** A drop from the Chasm: the stratum where it was won favours its slot (docs/SIMA.md, «Ranura favorecida»). */
const simaCtx = (s: CampaignState, p: NonNullable<ReturnType<typeof findPlayer>>, power: number, day: number, slot?: GearSlot): ReturnType<typeof lootCtx> => ({
  ...lootCtx(s, p, power, 'sima', day),
  ...(slot ? { favor: slot } : {}),
});

/** A piece's power from where it was won (docs/EQUIPO.md §4, docs/SIMA.md «Botín»): on top of the hero's level. */
export const simaPower = (floor: number): number => SIMA_POWER.base + imin(SIMA_POWER.cap - SIMA_POWER.base, idiv(floor, SIMA_POWER.per));

/**
 * A run of the Chasm for whoever is gathered (1 to 8 heroes), starting at `start` (1, or past a threshold the group
 * passed). Null while it is closed or the start is not one of the group's.
 */
export const simaParams = (s: CampaignState, pids: string[], day: number, start = 1): BattleParams | null => {
  if (!simaOpen(s) || !simaStarts(s).includes(start)) return null;
  const party = [...new Set(pids)]
    .filter((pid) => !!findPlayer(s, pid))
    .sort()
    .slice(0, SIMA.maxHeroes);
  const n = party.length || 1;
  const rec = simaRecord(s, day);
  const weekSeed = simaWeekSeed(s, rec.week);
  const seed = deriveSeed(weekSeed, 1 + (rec.runs % 4096) * 131 + start);
  const k = crowd(SIMA.partyBp, n);
  const heroes: BattleParams['heroes'] = party.map((pid, i) => {
    const p = findPlayer(s, pid) as NonNullable<ReturnType<typeof findPlayer>>;
    return {
      pid,
      name: heroName(p),
      cls: p.hero.heroClass,
      level: p.hero.level,
      control: 'live',
      powerBp: 10000,
      post: i,
      relics: p.hero.relics,
      awakened: p.hero.awakened ?? [],
      ...fightKit(p.hero, 'crypt', s),
    };
  });
  const map = simaMap();
  return snapshot({
    mode: 'dungeon',
    seed,
    map,
    structures: [],
    heroes,
    keepHp: 1,
    keepMaxHp: 1,
    marea: s.mareaIndex,
    wave: 0,
    spawns: [],
    dungeon: { spawns: [], bossHp: 0, dmgBp: 10000 },
    sima: {
      start,
      weekSeed,
      allowed: simaAllowed(s),
      countBp: mulBp(SIMA.baseCountBp, k.count),
      hpBp: mulBp(SIMA.baseHpBp, k.hpBp),
      guardianHp: bigParty(SIMA.guardianHp, n),
    },
    rules: { tier: 3 },
  });
};

/** What a run's chest holds for `done` floors finished from `start` (falling keeps half). */
export const simaChest = (start: number, done: number, fell: boolean): { xp: number; dust: number; embers: number; shards: number } => {
  const c = { xp: 0, dust: 0, embers: 0, shards: 0 };
  for (let f = start; f < start + done; f++) {
    const g = imin(f, SIMA_CHEST.capFloor);
    c.xp += SIMA_CHEST.xp + SIMA_CHEST.xpPerFloor * g;
    c.dust += SIMA_CHEST.dust + SIMA_CHEST.dustPerFloor * g;
    c.embers += SIMA_CHEST.embers;
    if (f >= SIMA_CHEST.shardsFrom) c.shards += SIMA_CHEST.shards;
  }
  if (!fell) return c;
  const h = SIMA_CHEST.fallBp;
  return {
    xp: mulBp(c.xp, h),
    dust: mulBp(c.dust, h),
    embers: mulBp(c.embers, h),
    shards: mulBp(c.shards, h),
  };
};

export interface SimaResult {
  /** Heroes who took the Chasm's piece of the day. */
  rewarded: string[];
  /** Floors finished in the run, and the deepest of them. */
  done: number;
  deepest: number;
  /** The group's mark went deeper (ever). */
  record: boolean;
}

/**
 * A finished run: each hero takes the chest (half if the party fell), a piece for every guardian felled (half of them,
 * the deepest first, if it fell), and once a day the Chasm's own piece and relic shot, only walking out. The group's
 * marks move; a new stone hangs on the winch for each stratum whose guardian fell for the first time.
 */
export const applySima = (s: CampaignState, battle: BattleState, day: number, now: Ms): SimaResult => {
  tallyDeeds(s, 'crypt', battle, now);
  afflictAfter(s, battle, now);
  const sm = battle.dungeon?.sima;
  // The Chasm from the floor a third of the hero's level is a hard place, and its guardians count (docs/UNICAS.md §2.2).
  if (sm) tallyFeats(s, battle, { sima: sm.start + imax(0, sm.done - 1) }, now);
  const rec = simaRecord(s, day);
  s.sima = rec;
  rec.open = true;
  rec.runs++;
  const pids = battle.heroes.map((h) => h.pid);
  if (!sm) return { rewarded: [], done: 0, deepest: 0, record: false };
  const fell = battle.result !== 'won';
  const done = sm.done;
  const deepest = done > 0 ? sm.start + done - 1 : 0;
  const chest = simaChest(sm.start, done, fell);
  // Thresholds passed: a piece each (falling keeps half of them, the deepest first).
  const pieces = sm.thresholds.slice().sort((a, b) => b - a);
  const kept = fell ? pieces.slice(0, idiv(pieces.length, 2)) : pieces;
  const rewarded: string[] = [];
  for (const pid of pids) {
    const p = findPlayer(s, pid);
    if (!p) continue;
    if (!rec.went.includes(pid)) rec.went.push(pid);
    grantXp(s, pid, chest.xp, now);
    if (chest.embers) p.hero.embers = (p.hero.embers ?? 0) + chest.embers;
    if (chest.shards) p.hero.shards = (p.hero.shards ?? 0) + chest.shards;
    // Amulets' scraps of the old garrison (docs/PANOPLIA.md §4): a threshold's guardian felled from the floor a third of
    // the hero's level, once a day per player.
    if (p.simaScrapDay !== day && kept.some((f) => f >= Math.trunc(p.hero.level / 3)) && addScraps(s, p, 'amulet', PANOPLY.scraps.sima, now) > 0) p.simaScrapDay = day;
    for (const f of kept)
      giveItem(s, p, rollItem(s, s.rng, f >= SIMA_LOOT.rareFrom ? 'rare' : 'uncommon', simaCtx(s, p, simaPower(f), day, STRATUM[simaFloor(sm.weekSeed, sm.order, f).stratum].slot)), now);
    // The Chasm's piece of the day, walking out only.
    if (!fell && done > 0 && p.simaDay !== day) {
      p.simaDay = day;
      rewarded.push(pid);
      const power = simaPower(deepest);
      const slot = STRATUM[simaFloor(sm.weekSeed, sm.order, deepest).stratum].slot;
      const veryRare =
        deepest >= SIMA_LOOT.veryRareFrom &&
        p.hero.level + power >= 20 &&
        rngChance(s.rng, imin(SIMA_LOOT.veryRareCapBp, SIMA_LOOT.veryRareBp + SIMA_LOOT.veryRarePerFloorBp * (deepest - SIMA_LOOT.veryRareFrom)));
      const rarity: Rarity = veryRare ? 'veryRare' : deepest >= SIMA_LOOT.rareFrom ? 'rare' : 'uncommon';
      giveItem(s, p, rollItem(s, s.rng, rarity, simaCtx(s, p, power, day, slot)), now);
      if (deepest >= SIMA_LOOT.relicFrom) rollRelic(s, pid, SIMA_LOOT.relicBp, now);
    }
    if (!fell && sm.thresholds.some((f) => STRATUM[simaFloor(sm.weekSeed, sm.order, f).stratum].guardian === 'colossus')) rollRelic(s, pid, RELIC_DROPS.colossus, now);
  }
  if (chest.dust) addRes(s.storage, { wood: 0, stone: 0, crystal: 0, dust: chest.dust });
  // The marks: this week's, and the group's ever (who set it, for the winch).
  if (deepest > rec.weekBest) rec.weekBest = deepest;
  const record = deepest > rec.best;
  if (record) {
    rec.best = deepest;
    rec.marks.push({
      floor: deepest,
      pids: [...new Set(pids)].sort(),
      at: now,
    });
    if (rec.marks.length > 40) rec.marks.splice(0, rec.marks.length - 40);
  }
  // A stone on the winch for each stratum whose guardian fell for the first time.
  for (const f of sm.thresholds.slice().sort((a, b) => a - b)) {
    const st = simaFloor(sm.weekSeed, sm.order, f).stratum;
    if (!rec.stones.some((x) => x.stratum === st)) rec.stones.push({ stratum: st, floor: f, at: now });
  }
  logOuting(s, now, 'sima', pids, !fell, deepest);
  event(s, now, 'sima', {
    data: {
      won: !fell,
      start: sm.start,
      done,
      deepest,
      record,
      party: pids.join(','),
    },
  });
  s.version++;
  return { rewarded, done, deepest, record };
};

/** The map every Chasm run uses (for tests and tools). */
export const SIMA_MAP_RADIUS = SIMA_RADIUS;
/** The week's order (for the card). */
export const simaWeekOrder = (s: CampaignState, day: number): number[] => simaOrder(simaWeekSeed(s, simaWeek(day)));
/** LOOT_POWER keeps one table: the Chasm's base sits in it too. */
export const simaLootBase = (): number => LOOT_POWER.deepCrypt;
export const simaMaxStart = (s: CampaignState): number => simaStarts(s).reduce((a, b) => imax(a, b), 1);
